import { Signer as Signer$1, utils, ethers } from 'ethers';
import { commons } from '@0xsequence/core';
import { isSignerStatusSigned } from '@0xsequence/signhub';
import { subDigestOf } from '@0xsequence/utils';
import { walletContracts } from '@0xsequence/abi';

// TODO: Move to account ?
class Signer extends Signer$1 {
  static isSequenceSigner(cand) {
    return isSequenceSigner(cand);
  }

  // abstract getWalletContext(): Promise<WalletContext>

  // abstract getWalletState(chainId?: ChainIdLike): Promise<WalletState[]>

  // getSigners returns a list of available / attached signers to the interface. Note: you need
  // enough signers in order to meet the signing threshold that satisfies a wallet config.
  // signMessage .....
  // signTypedData ..
  // sendTransaction takes an unsigned transaction, or list of unsigned transactions, and then has it signed by
  // the signer, and finally sends it to the relayer for submission to an Ethereum network.
  // sendTransactionBatch provides the ability to send an array/batch of transactions as a single native on-chain transaction.
  // This method works identically to sendTransaction but offers a different syntax for convience, readability and type clarity.
  // Low-level methods to sign and send/relayer signed transactions separately. The combination of these methods
  // is like calling just sendTransaction(..) above. Also note that sendSignedTransactions is identical
  // to calling getRelayer().relay(signedTxs), but included in this interface for convenience.
  // updateConfig will update the wallet image hash on-chain, aka deploying a smart wallet config to chain. If
  // newConfig argument is undefined, then it will use the existing config. Config contents will also be
  // automatically published to the authChain when updating the config image hash.
  // publishConfig will store the raw WalletConfig object on-chain, note: this may be expensive,
  // and is only necessary for config data-availability, in case of Account the contents are published
  // to the authChain.
  // isDeployed ..
}
function isSequenceSigner(signer) {
  const cand = signer;
  return cand && cand.updateConfig !== undefined && cand.publishConfig !== undefined && cand.getWalletConfig !== undefined;
}

// TODO: move to error.ts, along with others..
class InvalidSigner extends Error {}
class NotEnoughSigners extends Error {}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

async function resolveArrayProperties(object) {
  if (Array.isArray(object)) {
    // T must include array type
    return Promise.all(object.map(o => utils.resolveProperties(o)));
  }
  return utils.resolveProperties(object);
}
async function findLatestLog(provider, filter) {
  const toBlock = filter.toBlock === 'latest' ? await provider.getBlockNumber() : filter.toBlock;
  const fromBlock = filter.fromBlock;
  try {
    const logs = await provider.getLogs(_extends({}, filter, {
      toBlock: toBlock
    }));
    return logs.length === 0 ? undefined : logs[logs.length - 1];
  } catch (e) {
    // TODO Don't assume all errors are bad
    const pivot = Math.floor((toBlock - fromBlock) / 2 + fromBlock);
    const nhalf = await findLatestLog(provider, _extends({}, filter, {
      fromBlock: pivot,
      toBlock: toBlock
    }));
    if (nhalf !== undefined) return nhalf;
    return findLatestLog(provider, _extends({}, filter, {
      fromBlock: fromBlock,
      toBlock: pivot
    }));
  }
}

const statusToSignatureParts = status => {
  const parts = new Map();
  for (const signer of Object.keys(status.signers)) {
    const value = status.signers[signer];
    if (isSignerStatusSigned(value)) {
      const suffix = ethers.utils.arrayify(value.suffix);
      const suffixed = ethers.utils.solidityPack(['bytes', 'bytes'], [value.signature, suffix]);
      parts.set(signer, {
        signature: suffixed,
        isDynamic: suffix.length !== 1 || suffix[0] !== 2
      });
    }
  }
  return parts;
};
/**
 * The wallet is the minimum interface to interact with a single Sequence wallet/contract.
 * it doesn't have any knowledge of any on-chain state, instead it relies solely on the information
 * provided by the user. This building block is used to create higher level abstractions.
 *
 * Wallet can also be used to create Sequence wallets, but it's not recommended to use it directly
 * 
 * @notice: TODO: This class is meant to replace the one in ../wallet.ts !!!
 * 
 */
class Wallet extends ethers.Signer {
  constructor(options) {
    if (ethers.constants.Zero.eq(options.chainId) && !options.coders.signature.supportsNoChainId) {
      throw new Error(`Sequence version ${options.config.version} doesn't support chainId 0`);
    }
    super();
    this.context = options.context;
    this.config = options.config;
    this.orchestrator = options.orchestrator;
    this.coders = options.coders;
    this.address = options.address;
    this.chainId = options.chainId;
    this.provider = options.provider;
    this.relayer = options.relayer;
    this._reader = options.reader;
  }
  static newWallet(options) {
    const address = commons.context.addressOf(options.context, options.coders.config.imageHashOf(options.config));
    return new Wallet(_extends({}, options, {
      address
    }));
  }
  reader() {
    if (this._reader) return this._reader;
    if (!this.provider) throw new Error("Wallet status provider requires a provider");
    return new commons.reader.OnChainReader(this.provider);
  }
  setConfig(config) {
    this.config = config;
  }
  setOrchestrator(orchestrator) {
    this.orchestrator = orchestrator;
  }
  setAddress(address) {
    this.address = address;
  }
  getSigners() {
    return this.orchestrator.getSigners();
  }
  async getAddress() {
    return this.address;
  }
  async decorateTransactions(bundle) {
    if (await this.reader().isDeployed(this.address)) return bundle;
    const deployTx = this.buildDeployTransaction();

    // TODO: If entrypoint is guestModule we can flatten the bundle
    // and avoid calling guestModule twice

    return {
      entrypoint: this.context.guestModule,
      chainId: this.chainId,
      intent: bundle.intent,
      transactions: [...deployTx.transactions, {
        to: bundle.entrypoint,
        data: commons.transaction.encodeBundleExecData(bundle),
        gasLimit: 0,
        delegateCall: false,
        revertOnError: true,
        value: 0
      }]
    };
  }
  buildDeployTransaction() {
    const imageHash = this.coders.config.imageHashOf(this.config);
    if (commons.context.addressOf(this.context, imageHash) !== this.address) {
      throw new Error(`First address of config ${imageHash} doesn't match wallet address ${this.address}`);
    }
    return Wallet.buildDeployTransaction(this.context, imageHash);
  }
  deploy() {
    const deployTx = this.buildDeployTransaction();
    if (!this.relayer) throw new Error("Wallet deploy requires a relayer");
    return this.relayer.relay(_extends({}, deployTx, {
      chainId: this.chainId,
      intent: {
        id: ethers.utils.hexlify(ethers.utils.randomBytes(32)),
        wallet: this.address
      }
    }));
  }
  static buildDeployTransaction(context, imageHash) {
    const factoryInterface = new ethers.utils.Interface(walletContracts.factory.abi);
    return {
      entrypoint: context.guestModule,
      transactions: [{
        to: context.factory,
        data: factoryInterface.encodeFunctionData(factoryInterface.getFunction('deploy'), [context.mainModule, imageHash]),
        gasLimit: 100000,
        delegateCall: false,
        revertOnError: true,
        value: 0
      }]
    };
  }
  async buildUpdateConfigurationTransaction(config) {
    if (this.coders.config.update.isKindUsed) {
      const implementation = await this.reader().implementation(this.address);
      const isLaterUpdate = implementation && implementation === this.context.mainModuleUpgradable;
      return this.coders.config.update.buildTransaction(this.address, config, this.context, isLaterUpdate ? 'later' : 'first');
    }
    return this.coders.config.update.buildTransaction(this.address, config, this.context);
  }
  async signDigest(digest, request) {
    // The subdigest may be statically defined on the configuration
    // in that case we just encode the proof, no need to sign anything
    const subdigest = subDigestOf(this.address, this.chainId, digest);
    if (this.coders.config.hasSubdigest(this.config, subdigest)) {
      return this.coders.signature.encodeSigners(this.config, new Map(), [subdigest], this.chainId).encoded;
    }

    // We build the metadata object, this contains additional information
    // that may be needed to sign the digest (by the other signers, or by the guard)
    const metadata = _extends({
      digest,
      chainId: this.chainId,
      address: this.address,
      config: this.config
    }, request);

    // We ask the orchestrator to sign the digest, as soon as we have enough signature parts
    // to reach the threshold we returns true, that means the orchestrator will stop asking
    // and we can encode the final signature
    const subdigestBytes = ethers.utils.arrayify(subdigest);
    const signature = await this.orchestrator.signMessage({
      candidates: this.coders.config.signersOf(this.config).map(s => s.address),
      message: subdigestBytes,
      metadata,
      callback: (status, onNewMetadata) => {
        const parts = statusToSignatureParts(status);
        const newMetadata = _extends({}, metadata, {
          parts
        });
        onNewMetadata(newMetadata);
        return this.coders.signature.hasEnoughSigningPower(this.config, parts);
      }
    });
    const parts = statusToSignatureParts(signature);
    return this.coders.signature.encodeSigners(this.config, parts, [], this.chainId).encoded;
  }
  signMessage(message) {
    return this.signDigest(ethers.utils.keccak256(message), {
      message
    });
  }
  signTransactionBundle(bundle) {
    if (bundle.entrypoint !== this.address) {
      throw new Error(`Invalid entrypoint: ${bundle.entrypoint} !== ${this.address}`);
    }
    return this.signTransactions(bundle.transactions, bundle.nonce);
  }
  async signTransactions(txs, nonce) {
    const transaction = await resolveArrayProperties(txs);
    const transactions = commons.transaction.fromTransactionish(this.address, transaction);

    // NOTICE: If the `transactions` list is empty, then we add a dummy transaction
    // otherwise the `TxExecuted` event will not be emitted, and we won't be able to
    // find the transaction hash
    if (transactions.length === 0) {
      transactions.push({
        to: this.address,
        data: '0x',
        value: 0,
        gasLimit: 0,
        delegateCall: false,
        revertOnError: true
      });
    }
    let defaultedNonce = nonce;
    if (defaultedNonce === undefined) {
      defaultedNonce = await this.reader().nonce(this.address, 0);
      if (defaultedNonce === undefined) throw new Error("Unable to determine nonce");
    }
    const digest = commons.transaction.digestOfTransactions(defaultedNonce, transactions);
    const signature = await this.signDigest(digest, {
      transactions
    });
    return {
      intent: {
        // Maybe is better if signDigest returns the subdigest directly
        id: subDigestOf(this.address, this.chainId, digest),
        wallet: this.address
      },
      chainId: this.chainId,
      transactions,
      entrypoint: this.address,
      nonce: defaultedNonce,
      signature
    };
  }
  async sendSignedTransaction(signedBundle, quote) {
    if (!this.relayer) throw new Error("Wallet sendTransaction requires a relayer");
    return this.relayer.relay(signedBundle, quote);
  }
  async sendTransaction(txs, nonce, quote) {
    const signed = await this.signTransactions(txs, nonce);
    const decorated = await this.decorateTransactions(signed);
    return this.sendSignedTransaction(decorated, quote);
  }
  async fillGasLimits(txs) {
    const transaction = await resolveArrayProperties(txs);
    const transactions = commons.transaction.fromTransactionish(this.address, transaction);
    const relayer = this.relayer;
    if (!relayer) throw new Error("Wallet fillGasLimits requires a relayer");
    const simulations = await relayer.simulate(this.address, ...transactions);
    return transactions.map((tx, i) => {
      const gasLimit = tx.gasLimit ? ethers.BigNumber.from(tx.gasLimit).toNumber() : simulations[i].gasLimit;
      return _extends({}, tx, simulations[i], {
        gasLimit
      });
    });
  }
  connect(provider, relayer) {
    this.provider = provider;
    this.relayer = relayer;
    return this;
  }
  signTransaction(transaction) {
    throw new Error("Method not implemented.");
  }
}

// Implements a wrapper for using Sequence wallets as nested signers
// in the signhub orchestrator. It only works for nested signatures.
class SequenceOrchestratorWrapper {
  constructor(wallet) {
    this.wallet = wallet;
  }
  async getAddress() {
    return this.wallet.address;
  }
  async requestSignature(_id, message, metadata, callbacks) {
    if (!commons.isWalletSignRequestMetadata(metadata)) {
      throw new Error('SequenceOrchestratorWrapper only supports nested Sequence signatures');
    }

    // For Sequence nested signatures we must use `signDigest` and not `signMessage`
    // otherwise the wallet will hash the digest and the signature will be invalid.
    callbacks.onSignature(await this.wallet.signDigest(message, {
      nested: metadata
    }));
    return true;
  }
  notifyStatusChange(_i, _s, _m) {}
  suffix() {
    return [3];
  }
}

export { InvalidSigner, NotEnoughSigners, SequenceOrchestratorWrapper, Signer, Wallet, findLatestLog, isSequenceSigner, resolveArrayProperties };
